"use client";

import React, { useMemo } from "react";
import { BaseChart } from "./base-chart";
import { chartTooltip, chartGrid, chartCategoryAxis, chartValueAxis, chartAnimation, formatChartValue } from "./chart-utils";
import { resolveChartTokens } from "@/src/utils/chart-theme";

export interface WaterfallChartData {
  items: { name: string; value: number; isTotal?: boolean }[];
}

export interface WaterfallChartProps {
  data: WaterfallChartData;
  title?: string;
  subtitle?: string;
  height?: string | number;
  className?: string;
  showLegend?: boolean;
  showValues?: boolean;
  loading?: boolean;
  empty?: boolean;
  positiveColor?: string;
  negativeColor?: string;
  totalColor?: string;
  formatValue?: (value: number) => string;
}

export function WaterfallChart({
  data,
  title,
  subtitle,
  height = 350,
  className,
  showValues = true,
  loading = false,
  empty = false,
  positiveColor,
  negativeColor,
  totalColor,
  formatValue = (v) => formatChartValue(v, 0),
}: WaterfallChartProps) {
  const option = useMemo(() => {
    const tokens = resolveChartTokens();
    const posColor = positiveColor || tokens.successColor;
    const negColor = negativeColor || tokens.errorColor;
    const totColor = totalColor || tokens.colors[0];

    const categories: string[] = [];
    const baseData: (number | string)[] = [];
    const valueData: (number | { value: number; itemStyle: any; label?: any })[] = [];
    let runningTotal = 0;

    data.items.forEach((item) => {
      categories.push(item.name);
      if (item.isTotal) {
        baseData.push(0);
        valueData.push({ value: runningTotal, itemStyle: { color: totColor, borderRadius: [4, 4, 4, 4] } });
      } else if (item.value >= 0) {
        baseData.push(runningTotal);
        runningTotal += item.value;
        valueData.push({ value: item.value, itemStyle: { color: posColor, borderRadius: [4, 4, 0, 0] } });
      } else {
        runningTotal += item.value;
        baseData.push(runningTotal);
        valueData.push({ value: Math.abs(item.value), itemStyle: { color: negColor, borderRadius: [4, 4, 0, 0] } });
      }
    });

    return {
      ...(title ? { title: { text: title, subtext: subtitle || "", left: 0, textStyle: { fontSize: 16, fontWeight: 600, color: tokens.textColor, fontFamily: tokens.fontFamily }, subtextStyle: { fontSize: 12, color: tokens.textColorSecondary, fontFamily: tokens.fontFamily } } } : {}),
      ...chartTooltip({
        trigger: "axis",
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          if (idx === undefined) return "";
          const item = data.items[idx];
          const prefix = item.isTotal ? "Total" : item.value >= 0 ? "+" : "";
          return `${item.name}<br/>${prefix}${formatValue(item.value)}`;
        },
      }),
      ...chartGrid({ bottom: 24 }),
      ...chartAnimation({ delay: (idx: number) => idx * 100 }),
      xAxis: chartCategoryAxis(categories),
      yAxis: chartValueAxis(),
      series: [
        {
          type: "bar" as const,
          stack: "waterfall" as const,
          silent: true,
          itemStyle: { color: "transparent", borderColor: "transparent" },
          emphasis: { itemStyle: { color: "transparent" } },
          data: baseData,
        },
        {
          type: "bar" as const,
          stack: "waterfall" as const,
          barMaxWidth: 40,
          label: showValues ? {
            show: true,
            position: "top" as const,
            formatter: (p: any) => {
              const item = data.items[p.dataIndex];
              return item.isTotal ? formatValue(item.value) : (item.value >= 0 ? "+" : "") + formatValue(item.value);
            },
            fontSize: 11,
            fontFamily: tokens.fontFamily,
            color: tokens.textColor,
          } : { show: false },
          data: valueData,
        },
      ],
    };
  }, [data, title, subtitle, showValues, positiveColor, negativeColor, totalColor, formatValue]);

  return (
    <BaseChart
      option={option}
      height={height}
      className={className}
      loading={loading}
      empty={empty || data.items.length === 0}
    />
  );
}

export default WaterfallChart;
